import { useEffect, useRef, useState, useCallback } from "react";
import { renderWidget } from "@/shared/dashboard/widgets/registry";
import type { WidgetId } from "@/shared/dashboard/widgets/types";

interface Props {
  widgets: Array<{ widget: WidgetId }>;
  /** Label shown on the peek handle when drawer is closed. Defaults to "Patients & Conversations". */
  peekLabel?: string;
  /** Fixed pixel offset from viewport bottom to leave room for BottomNav. Default 64 + safe-area. */
  bottomOffsetCss?: string;
}

const HANDLE_HEIGHT = 44; // px — visible peek zone above BottomNav when closed
const OPEN_HEIGHT_DVH = 72;
const DRAG_OPEN_THRESHOLD = 60; // px — drag up > this opens; drag down > this closes
const SPRING_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

/**
 * Mobile-only bottom sheet with a draggable peek handle. Sits above the
 * BottomNav. Drag up to open, drag down (or tap outside) to close.
 * Widgets render identically to their desktop sideRail treatment.
 */
export function MobileRailDrawer({
  widgets,
  peekLabel = "Patients & Conversations",
  // Matches BottomNav height: 56px button row + max(8px, safe-area-inset-bottom) padding.
  bottomOffsetCss = "calc(max(8px, env(safe-area-inset-bottom)) + 56px)",
}: Props) {
  const [open, setOpen] = useState(false);
  const [dragY, setDragY] = useState(0); // live drag delta in px (negative = up)
  const [dragging, setDragging] = useState(false);
  const startYRef = useRef<number | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setDragY(0);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onEsc);
    };
  }, [open, close]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    startYRef.current = e.clientY;
    setDragging(true);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (startYRef.current == null) return;
    const delta = e.clientY - startYRef.current;
    // When open, allow dragging DOWN (delta > 0); cap upward (delta < 0) at 0.
    // When closed, allow dragging UP (delta < 0); cap downward (delta > 0) at 0.
    if (open) {
      setDragY(Math.max(0, delta));
    } else {
      setDragY(Math.min(0, delta));
    }
  };

  const onPointerUp = () => {
    if (startYRef.current == null) return;
    const threshold = DRAG_OPEN_THRESHOLD;
    if (!open && dragY < -threshold) setOpen(true);
    else if (open && dragY > threshold) setOpen(false);
    setDragY(0);
    setDragging(false);
    startYRef.current = null;
  };

  const handleTap = () => {
    // Treat a near-zero drag as a tap toggle.
    if (Math.abs(dragY) < 5) setOpen((v) => !v);
  };

  // Compute the sheet's translateY:
  // closed (resting): translateY = calc(100% - HANDLE_HEIGHT) → only handle peeks
  // open:   translateY = 0
  // during drag: add dragY so the sheet tracks the finger.
  const restingTranslateY = open ? 0 : `calc(100% - ${HANDLE_HEIGHT}px)`;
  const sheetTransform = dragging
    ? `translateY(calc(${restingTranslateY} + ${dragY}px))`
    : `translateY(${restingTranslateY})`;

  const backdropVisible = open && !dragging;

  return (
    <div
      className="md:hidden pointer-events-none fixed inset-0 z-[70]"
      aria-hidden={!open && !dragging}
    >
      {/* Backdrop — stops at BottomNav top so the tab bar stays fully visible. */}
      <div
        onClick={close}
        className="absolute left-0 right-0 top-0 bg-[var(--fg)] transition-opacity duration-300"
        style={{
          bottom: bottomOffsetCss,
          opacity: backdropVisible
            ? 0.25
            : dragging && dragY < 0
              ? Math.min(0.25, Math.abs(dragY) / 300)
              : 0,
          pointerEvents: open ? "auto" : "none",
        }}
      />

      {/* Sheet clipper — stops at BottomNav top so the sheet cannot paint over the tab bar. */}
      <div
        className="absolute left-0 right-0 top-0 overflow-hidden pointer-events-none"
        style={{ bottom: bottomOffsetCss }}
      >
        {/* Sheet */}
        <div
          role="dialog"
          aria-modal={open}
          aria-label={peekLabel}
          className="absolute left-0 right-0 bottom-0 bg-[var(--bg)] rounded-t-[calc(var(--radius)*3)] border-t border-[var(--border)] shadow-[0_-10px_40px_-12px_rgba(10,10,10,0.15)] flex flex-col pointer-events-auto"
          style={{
            height: `${OPEN_HEIGHT_DVH}dvh`,
            transform: sheetTransform,
            transition: dragging ? "none" : `transform 320ms ${SPRING_EASE}`,
            touchAction: "none",
          }}
        >
          {/* Peek / drag handle — tap or drag. Always visible. */}
          <div
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onClick={handleTap}
            className="shrink-0 flex flex-col items-center justify-center cursor-grab active:cursor-grabbing select-none"
            style={{ height: HANDLE_HEIGHT }}
          >
            <div className="w-10 h-1 rounded-full bg-[var(--border)] mb-1.5" />
            <div className="text-[11px] uppercase tracking-[0.1em] text-[var(--muted)]">
              {peekLabel}
            </div>
          </div>

          {/* Widgets — each in its own flex slot so both share height and scroll independently */}
          <div className="flex-1 min-h-0 flex flex-col border-t border-[var(--border)]">
            {widgets.map(({ widget }, i) => (
              <div
                key={`${widget}-${i}`}
                className={`flex flex-col min-h-0 ${
                  widgets.length > 1 ? "flex-1 basis-0" : "flex-1"
                } ${i > 0 ? "border-t border-[var(--border)]" : ""}`}
              >
                {renderWidget(widget, { slot: "sideRail" })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
